/**
 * Orders Form Module
 *
 * Handles order creation/editing functionality including:
 * - Product search and selection
 * - Order item management
 * - Totals calculation
 */

import { $, $$, ready } from '../utils/dom.js';
import { getToken } from '../utils/csrf.js';

// =============================================================================
// State
// =============================================================================

let orderItems = [];
let allProducts = [];
let allCategories = [];

// =============================================================================
// Item Modal
// =============================================================================

/**
 * Open item selection modal
 */
export function openItemModal() {
    const modal = $('#item-modal');
    if (modal) {
        modal.classList.remove('hidden');
        loadProducts();
    }
}

/**
 * Close item selection modal
 */
export function closeItemModal() {
    const modal = $('#item-modal');
    if (modal) modal.classList.add('hidden');
}

/**
 * Load products for selection
 */
export async function loadProducts() {
    const container = $('#products-grid');
    if (!container) return;

    container.innerHTML = '<p class="col-span-full text-center text-gray-500 py-4">Loading products...</p>';

    try {
        const response = await fetch('/api/v1/products');
        const data = await response.json();

        if (data.success) {
            allProducts = data.products || [];
            allCategories = data.categories || [];
            renderProducts(allProducts);
            renderCategories(allCategories);
        }
    } catch (error) {
        console.error('Error loading products:', error);
        container.innerHTML = '<p class="col-span-full text-center text-red-500 py-4">Error loading products</p>';
    }
}

/**
 * Render products grid
 * @param {Array} products
 */
function renderProducts(products) {
    const container = $('#products-grid');
    if (!container) return;

    if (products.length === 0) {
        container.innerHTML = '<p class="col-span-full text-center text-gray-500 py-4">No products found</p>';
        return;
    }

    container.innerHTML = products.map(product => `
        <div class="product-item p-3 border rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50"
             onclick="selectProduct('${product.id}', '${product.name.replace(/'/g, "\\'")}', ${product.price})">
            <div class="font-medium text-sm">${product.name}</div>
            <div class="text-gray-500 text-xs">$${parseFloat(product.price).toFixed(2)}</div>
        </div>
    `).join('');
}

/**
 * Render category filter options
 * @param {Array} categories
 */
function renderCategories(categories) {
    const select = $('#category-filter');
    if (!select) return;

    select.innerHTML = '<option value="">All Categories</option>' +
        categories.map(cat => `<option value="${cat.id}">${cat.name}</option>`).join('');
}

/**
 * Filter products by search and category
 */
export function filterProducts() {
    const search = $('#product-search')?.value.toLowerCase() || '';
    const categoryId = $('#category-filter')?.value || '';

    const filtered = allProducts.filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(search);
        const matchesCategory = !categoryId || product.category_id === categoryId;
        return matchesSearch && matchesCategory;
    });

    renderProducts(filtered);
}

/**
 * Select a product to add to order
 * @param {string} productId
 * @param {string} name
 * @param {number} price
 */
export function selectProduct(productId, name, price) {
    const quantity = parseInt($('#item-quantity')?.value) || 1;

    orderItems.push({
        product_id: productId,
        name: name,
        price: price,
        quantity: quantity
    });

    updateOrderItemsDisplay();
    updateTotals();
    closeItemModal();
}

// =============================================================================
// Order Items Display
// =============================================================================

/**
 * Update the order items display
 */
export function updateOrderItemsDisplay() {
    const container = $('#order-items-list');
    const emptyState = $('#order-items-empty');
    const itemsContainer = $('#order-items-container');

    if (!container) return;

    if (orderItems.length === 0) {
        if (emptyState) emptyState.classList.remove('hidden');
        if (itemsContainer) itemsContainer.classList.add('hidden');
        return;
    }

    if (emptyState) emptyState.classList.add('hidden');
    if (itemsContainer) itemsContainer.classList.remove('hidden');

    container.innerHTML = orderItems.map((item, index) => `
        <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
                <div class="font-medium">${item.name}</div>
                <div class="text-sm text-gray-500">$${item.price.toFixed(2)} x ${item.quantity}</div>
            </div>
            <div class="flex items-center gap-3">
                <span class="font-medium">$${(item.price * item.quantity).toFixed(2)}</span>
                <button type="button" onclick="removeOrderItem(${index})" class="text-red-500 hover:text-red-700">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                </button>
            </div>
        </div>
    `).join('');

    // Update hidden input with items JSON
    const itemsInput = $('#order_items_json');
    if (itemsInput) {
        itemsInput.value = JSON.stringify(orderItems);
    }
}

/**
 * Remove an item from the order
 * @param {number} index
 */
export function removeOrderItem(index) {
    orderItems.splice(index, 1);
    updateOrderItemsDisplay();
    updateTotals();
}

// =============================================================================
// Totals Calculation
// =============================================================================

/**
 * Update order totals
 */
export function updateTotals() {
    const subtotal = orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const deliveryFee = parseFloat($('#delivery_fee')?.value) || 0;
    const taxRate = parseFloat($('#tax_rate')?.value) || 0;
    const tax = subtotal * (taxRate / 100);
    const total = subtotal + deliveryFee + tax;

    const subtotalEl = $('#order-subtotal');
    const taxEl = $('#order-tax');
    const deliveryEl = $('#order-delivery');
    const totalEl = $('#order-total');

    if (subtotalEl) subtotalEl.textContent = '$' + subtotal.toFixed(2);
    if (taxEl) taxEl.textContent = '$' + tax.toFixed(2);
    if (deliveryEl) deliveryEl.textContent = '$' + deliveryFee.toFixed(2);
    if (totalEl) totalEl.textContent = '$' + total.toFixed(2);
}

// =============================================================================
// Auto-Initialization
// =============================================================================

function initOrdersForm() {
    const form = $('#order-create-form') || $('#order-edit-form');
    if (!form) return;

    // Add item button
    const addItemBtn = $('#add-item-btn');
    if (addItemBtn) {
        addItemBtn.addEventListener('click', openItemModal);
    }

    // Delivery fee change
    const deliveryFeeSelect = $('#delivery_fee');
    if (deliveryFeeSelect) {
        deliveryFeeSelect.addEventListener('change', updateTotals);
    }

    // Product search
    const productSearch = $('#product-search');
    const categoryFilter = $('#category-filter');
    if (productSearch) productSearch.addEventListener('input', filterProducts);
    if (categoryFilter) categoryFilter.addEventListener('change', filterProducts);

    // Keyboard shortcuts
    document.addEventListener('keydown', function(e) {
        if ((e.ctrlKey || e.metaKey) && e.key === 's') {
            e.preventDefault();
            form.submit();
        }
        if (e.key === 'Escape') {
            closeItemModal();
        }
    });

    updateOrderItemsDisplay();
}

ready(initOrdersForm);

// Expose globally
window.openItemModal = openItemModal;
window.closeItemModal = closeItemModal;
window.filterProducts = filterProducts;
window.selectProduct = selectProduct;
window.removeOrderItem = removeOrderItem;
window.updateTotals = updateTotals;

export default {
    openItemModal,
    closeItemModal,
    loadProducts,
    filterProducts,
    selectProduct,
    removeOrderItem,
    updateTotals
};
